import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import getIcon from "@/lib/iconMap";

// Shared activity layout: sticky activity header + left sidebar section nav +
// central content column + optional right rail. Fully responsive:
//  - Desktop: persistent sidebar (and right rail where provided)
//  - Mobile: sidebar collapses into a horizontal scrollable tab bar; the
//    right rail stacks inline above the content.
export default function ActivityShell({
  title,
  eyebrow,
  backTo,
  backLabel = "Back",
  nav = [],
  activeKey,
  onSelect,
  rightRail = null,
  headerRight = null,
  showSupport = true,
  children,
}) {
  const hasRail = !!rightRail;
  return (
    <div className="bg-white">
      {/* Sticky activity header */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-brand-navy/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              {backTo && (
                <Link
                  to={backTo}
                  data-testid="activity-back"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy/60 hover:text-brand-orange transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
                </Link>
              )}
              <h1 className="display-title text-lg sm:text-2xl text-brand-navy truncate leading-tight">{title}</h1>
              {eyebrow && <p className="text-xs sm:text-sm text-brand-orange font-semibold -mt-0.5">{eyebrow}</p>}
            </div>
            {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
          </div>

          {/* Mobile horizontal tab bar */}
          {nav.length > 0 && (
            <nav className="lg:hidden mt-2 -mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="mobile-tabs">
              {nav.map((item) => {
                const Icon = getIcon(item.icon);
                const active = item.key === activeKey;
                return (
                  <button
                    key={item.key}
                    data-testid={`tab-${item.key}`}
                    onClick={() => onSelect?.(item.key)}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold min-h-[40px] whitespace-nowrap transition-colors ${
                      active ? "bg-brand-orange text-white" : "bg-brand-cream text-brand-navy"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {item.done && <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />}
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
        <div
          className={`grid gap-6 lg:gap-8 ${
            hasRail
              ? "lg:grid-cols-[220px_minmax(0,1fr)_300px]"
              : "lg:grid-cols-[220px_minmax(0,1fr)]"
          }`}
        >
          {/* Desktop sidebar */}
          {nav.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-40 space-y-1.5">
                {nav.map((item) => {
                  const Icon = getIcon(item.icon);
                  const active = item.key === activeKey;
                  return (
                    <button
                      key={item.key}
                      data-testid={`nav-${item.key}`}
                      onClick={() => onSelect?.(item.key)}
                      className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-left text-sm font-semibold transition-colors ${
                        active
                          ? "bg-brand-orange text-white"
                          : "text-brand-navy hover:bg-brand-cream"
                      }`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-white" : "text-brand-orange"}`} />
                      <span className="flex-1">{item.label}</span>
                      {item.done && (
                        <span
                          className={`w-2 h-2 rounded-full ${active ? "bg-white" : "bg-brand-orange"}`}
                          aria-label="complete"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </aside>
          )}

          {/* Content + rail */}
          {hasRail ? (
            <>
              {/* Rail stacks above content on mobile, sits right on desktop */}
              <div className="min-w-0 order-2 lg:order-1">{children}</div>
              <div className="order-1 lg:order-2">
                <div className="lg:sticky lg:top-40">{rightRail}</div>
              </div>
            </>
          ) : (
            <div className="min-w-0">{children}</div>
          )}
        </div>
      </div>
    </div>
  );
}
